import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useTripleWhalePageView } from "@/hooks/useTripleWhalePageView";
import { PixelTracker } from "@/hooks/usePixelTracker";
import Index from "./pages/Index";
import { captureTrackingParams } from "@/lib/tracking";

// Capture tracking params synchronously on module load, before any render.
if (typeof window !== "undefined") captureTrackingParams();

const Dashboard = lazy(() => import("./pages/Dashboard"));
const DashboardLogin = lazy(() => import("./pages/DashboardLogin"));
const Creatives = lazy(() => import("./pages/Creatives"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const AppInner = () => {
  useTripleWhalePageView();
  useEffect(() => { captureTrackingParams(); }, []);

  return (
    <>
      <PixelTracker />
      <Suspense fallback={<div className="min-h-screen" />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/dashboard-login" element={<DashboardLogin />} />
        <Route path="/creatives" element={<Creatives />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppInner />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
